import { useSyncExternalStore } from 'react';
import NetInfo from '@react-native-community/netinfo';
import { focusManager, onlineManager } from '@tanstack/react-query';
import { AppState, Platform, type AppStateStatus } from 'react-native';

// Issue #152: TanStack Query's onlineManager defaults to the WEB
// platform's navigator.onLine/window 'online'/'offline' events, which
// don't exist in React Native -- without this wiring, every query and
// mutation's default networkMode: 'online' would see the app as
// permanently online no matter the real connection. Once wired, this
// is what gives every existing (and future) mutation its retry-on-
// reconnect behavior for free: a mutation fired while offline is
// automatically held 'paused' rather than executed, and every paused
// mutation resumes on its own the instant this listener reports back
// online -- see the TanStack Query React Native guide, "isOnline". No
// hand-rolled queue needed; the library already does this once it
// knows the real connectivity state.
//
// Called once, at import time, from the app's root layout -- calling
// it more than once would just replace the same listener redundantly.
export function setUpNetworkStatusListener() {
  onlineManager.setEventListener((setOnline) => {
    return NetInfo.addEventListener((state) => {
      setOnline(!!state.isConnected);
    });
  });
}

// The one place UI reads live connectivity from -- onlineManager is
// already the single source of truth once setUpNetworkStatusListener
// has run (queries/mutations key off it internally too), so this
// doesn't add a second NetInfo subscription of its own.
export function useIsOnline(): boolean {
  return useSyncExternalStore(
    (callback) => onlineManager.subscribe(callback),
    () => onlineManager.isOnline(),
  );
}

// Issue #395: TanStack's focusManager defaults to the WEB document's
// visibilitychange events, which don't exist in React Native -- without
// this wiring, "don't refetch/refetch-interval in the background" (every
// query's default, and specifically the /me/activity badge poller's
// nothing-polls-while-backgrounded requirement) can't see backgrounding
// on native. AppState IS React Native's document.visibilityState
// equivalent; wiring it here -- once, at app root, next to the
// onlineManager listener -- also makes every other query's default
// refetchOnWindowFocus do the right thing on return to foreground. On
// web the library's own visibilitychange listener is already correct,
// so this is a no-op there. App-lifetime listener, like the one above:
// registered once, never removed.
export function setUpAppStateFocusListener() {
  if (Platform.OS === 'web') return;

  focusManager.setFocused(true);
  AppState.addEventListener('change', (status: AppStateStatus) => {
    focusManager.setFocused(status === 'active');
  });
}
